import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../state/useSession';
import { useProjects } from '../state/projects';
import { isUnfinished, isWebApp } from '@/webapp/webAppRecord';
import { siteLink } from '@/webapp/siteAddress';
import { timeAgo } from '@/utils/time';
import type { AppDefinition } from '@/types/app';
import { Chip, EmptyState, FAB, IdentityChip, Screen } from '../ui';

/** Ported from src/app/(main)/(tabs)/index.tsx — the projects you are building. */
export default function Home() {
  const navigate = useNavigate();
  const { user } = useSession();
  const projects = useProjects((s) => s.projects);
  const isLoading = useProjects((s) => s.isLoading);

  useEffect(() => {
    void useProjects.getState().loadProjects(user?.id ?? '');
  }, [user?.id]);

  return (
    <Screen title="Build">
      {projects.length === 0 && !isLoading ? (
        <EmptyState
          icon="emptyApps"
          title="No projects yet"
          body="Describe an app in a sentence and Saddle builds it."
          ctaTitle="Create an app"
          onCta={() => navigate('/new/idea')}
        />
      ) : (
        <ul className="project-list app-grid">
          {projects.map((p) => {
            const definition = p.definition as AppDefinition;
            // Where the page lives; null for an app with no page, or a build that did not land.
            const site = siteLink(definition);
            return (
              <li key={p.id} className="project-item">
                <button
                  type="button"
                  className="card clay-card pressable project-row"
                  onClick={() => navigate(`/project/${p.id}`)}
                >
                  <IdentityChip name={p.name} />
                  <span className="project-text">
                    <span className="type-heading">{p.name}</span>
                    <span className="type-caption project-meta">{timeAgo(p.updated_at)}</span>
                  </span>
                  {definition.webUrl && <Chip label="Live" tone="success" />}
                  {/* A build that did not land keeps its row; this says why it is there. */}
                  {isUnfinished(definition) && <Chip label="Not built yet" tone="neutral" />}
                  {!isWebApp(definition) && <Chip label="Phone only" tone="neutral" />}
                </button>
                {/*
                  * Beside the row, not inside it: a link inside a button is invalid HTML, and the
                  * row already opens the editor. The website was a click into the editor and a
                  * scroll past the preview away; from the list it is now one click, as on the phone.
                  */}
                {site && (
                  <a
                    className="project-open"
                    href={site.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open ${p.name} in a new tab`}
                  >
                    Open <span aria-hidden="true">↗</span>
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <FAB icon="add" onPress={() => navigate('/new/idea')} accessibilityLabel="New app" />
    </Screen>
  );
}
